import { Component, type ErrorInfo, type ReactNode } from "react";

/**
 * Top-level error boundary. A render/runtime error in any page must never blank the
 * whole SPA — we catch it, log it to the console for support, and show a recoverable
 * fallback with a reload action instead of an empty white screen. No CUI is rendered
 * in the fallback (just the error message text the app itself produced).
 */
interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface to the console so it reaches Cloud Logging via the browser and any
    // support screen-share; we intentionally do not POST CUI anywhere.
    console.error("Portal render error:", error, info.componentStack);
  }

  private handleReload = (): void => {
    this.setState({ error: null });
    window.location.reload();
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div className="flex min-h-full items-center justify-center px-6 py-16">
        <div className="max-w-md rounded-md border border-accent/30 bg-surface p-6 text-center">
          <h1 className="text-base font-semibold text-ink">Something went wrong</h1>
          <p className="mt-2 text-sm text-ink-muted">
            The portal hit an unexpected error and could not render this page. Your data is
            safe. Try reloading; if it keeps happening, contact support.
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            className="mt-4 inline-flex items-center justify-center rounded-sm border border-accent/40 bg-accent/10 px-4 py-2 text-sm font-medium text-accent-strong hover:bg-accent/20"
          >
            Reload the portal
          </button>
        </div>
      </div>
    );
  }
}
